import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MessageSquare, X, Send, Trash2, Loader2 } from 'lucide-react';
import { startChat, fetchSession, sendMessage } from '../api/chatApi';

// Cross-device chat backed by FastAPI + MongoDB.
// Polls the active session every 2 seconds while the panel is open.

const SID_KEY = 'likith_chat_sid_v2';
const NAME_KEY = 'likith_chat_name_v2';

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(localStorage.getItem(NAME_KEY) || '');
  const [sessionId, setSessionId] = useState(localStorage.getItem(SID_KEY) || '');
  const [stage, setStage] = useState(sessionId ? 'chat' : 'intro'); // intro | chat | ended
  const [session, setSession] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const bodyRef = useRef(null);

  // Open on global event
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('open-chat', handler);
    return () => window.removeEventListener('open-chat', handler);
  }, []);

  // Poll the session while open
  const refresh = useCallback(async () => {
    if (!sessionId) return;
    try {
      const data = await fetchSession(sessionId);
      if (!data || data.exists === false) {
        // Owner ended chat — wiped on server
        setStage('ended');
        setSession(null);
        return;
      }
      setSession(data);
      if (data.endedAt) setStage('ended');
    } catch (e) {
      // Silently fail; don't spam errors
      // console.error(e);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!open || !sessionId) return;
    refresh();
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
  }, [open, sessionId, refresh]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  });

  const onStart = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true); setError('');
    try {
      const s = await startChat(name.trim());
      setSessionId(s.id);
      setSession(s);
      localStorage.setItem(SID_KEY, s.id);
      localStorage.setItem(NAME_KEY, name.trim());
      setStage('chat');
    } catch (err) {
      setError('Could not start chat. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const onSend = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !sessionId || busy) return;
    const text = draft.trim();
    setDraft('');
    setBusy(true); setError('');
    try {
      await sendMessage(sessionId, 'visitor', text);
      await refresh();
    } catch (err) {
      const code = err && err.response && err.response.status;
      if (code === 410 || code === 404) { setStage('ended'); }
      else { setError('Could not send. Try again.'); setDraft(text); }
    } finally {
      setBusy(false);
    }
  };

  const startNewChat = () => {
    localStorage.removeItem(SID_KEY);
    setSessionId('');
    setSession(null);
    setStage('intro');
    setError('');
  };

  const headerSubtitle = stage === 'chat' && session ? `Hey ${session.name}` : 'Send a message';

  return (
    <>
      <div className="chat-fab" onClick={() => setOpen((v) => !v)} role="button" aria-label="Open chat">
        {open ? <X size={20} /> : <MessageSquare size={20} />}
      </div>

      {open && (
        <div className="chat-panel">
          <div className="chat-header">
            <div>
              <div
                className="mono"
                style={{ fontSize: 9, letterSpacing: '0.35em', color: 'rgba(245,245,245,0.5)', textTransform: 'uppercase' }}
              >
                Live chat &middot; with Likith
              </div>
              <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 16, color: '#f5f5f5', marginTop: 2 }}>
                {headerSubtitle}
              </div>
            </div>
            <X size={18} style={{ cursor: 'pointer', color: 'rgba(245,245,245,0.6)' }} onClick={() => setOpen(false)} />
          </div>

          {stage === 'intro' && (
            <form onSubmit={onStart} className="chat-form" style={{ flexDirection: 'column', borderTop: 'none', padding: 22, gap: 14 }}>
              <p style={{ color: 'rgba(245,245,245,0.65)', fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>
                Drop your name to start. Once we&apos;re done I close the chat and the whole
                thread is wiped &mdash; nothing kept.
              </p>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoFocus
                disabled={busy}
              />
              {error && <div style={{ color: '#ff8a8a', fontSize: 12 }}>{error}</div>}
              <button type="submit" disabled={busy}>
                {busy ? 'Starting\u2026' : 'Start chat'}
              </button>
            </form>
          )}

          {stage === 'chat' && session && (
            <>
              <div className="chat-body" ref={bodyRef}>
                <div className="bubble system">
                  Chat started &middot; {session.createdAt ? new Date(session.createdAt).toLocaleTimeString() : ''}
                </div>
                {(session.messages || []).map((m) => (
                  <div key={m.id} className={`bubble ${m.sender === 'visitor' ? 'me' : 'them'}`}>
                    {m.text}
                  </div>
                ))}
                {(!session.messages || session.messages.length === 0) && (
                  <div className="bubble system">No messages yet &mdash; type below to start</div>
                )}
              </div>
              <form onSubmit={onSend} className="chat-form">
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type a message…"
                  disabled={busy}
                />
                <button type="submit" aria-label="Send" disabled={busy}>
                  {busy ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
                </button>
              </form>
              {error && (
                <div style={{ padding: '8px 14px', color: '#ff8a8a', fontSize: 11, textAlign: 'center' }}>
                  {error}
                </div>
              )}
            </>
          )}

          {stage === 'ended' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 28, textAlign: 'center' }}>
              <Trash2 size={28} color="rgba(245,245,245,0.5)" />
              <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 18, color: '#f5f5f5' }}>Thread closed &amp; wiped.</div>
              <div style={{ color: 'rgba(245,245,245,0.55)', fontSize: 13, lineHeight: 1.6 }}>
                Likith ended the conversation. All messages have been deleted forever.
              </div>
              <button onClick={startNewChat} className="btn-ghost" style={{ marginTop: 8, padding: '10px 18px' }}>
                Start new chat
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
